import React, { useId, useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';

/**
 * Password input with a show/hide toggle, matching the .auth-field markup used
 * across the auth screens.
 *
 * The toggle is a real <button type="button"> so it never submits the form, and
 * it sits outside the <label> — a click inside the label would re-focus the
 * input and fight the toggle. State is per-field: revealing one password should
 * not reveal the confirmation field next to it.
 */
export function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete = 'current-password',
  required = true,
  minLength,
  id,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  id?: string;
}) {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="auth-field has-toggle">
      <label className="auth-field-main" htmlFor={inputId}>
        <Lock size={17} aria-hidden="true" />
        <span className="sr-only">{label}</span>
        <input
          id={inputId}
          type={visible ? 'text' : 'password'}
          required={required}
          minLength={minLength}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
      </button>
    </div>
  );
}
